import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  capitalize,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { isBefore } from "date-fns";
import { Formik, Form, Field, FormikProps } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";

import { useT } from "../../i18n";
import { FreeDay, FreeDayCreate, FreeDayUpdate } from "../../api";
import { useCreateFreeDay, useUpdateFreeDay } from "../../client/hooks";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/de";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router";
import { Select } from "formik-mui";
import FreeDayMessage from "./FreeDayMessage";

interface Props {
  open: boolean;
  freeDay?: FreeDay | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  label: string;
  dateFrom: dayjs.Dayjs | null;
  dateTo: dayjs.Dayjs | null;
  brzKey: string;
}

export default function FreeDayDialog({
  open,
  freeDay,
  onClose,
  onSuccess,
}: Props) {
  const { t: translation } = useT("freedays");
  const { enqueueSnackbar } = useSnackbar();
  const createFreeDay = useCreateFreieZeit();
  const updateFreeDay = useSaveFreieZeit(freeDay?.id ?? "");

  const isEdit = Boolean(freeDay);

  const initialValues: FormValues = {
    label: freeDay?.label || "",
    dateFrom: freeDay?.dateFrom ? dayjs(freeDay.dateFrom) : null,
    dateTo: freeDay?.dateTo ? dayjs(freeDay.dateTo) : null,
    brzKey: freeDay?.brzKey || "U",
  };

  const validationSchema = Yup.object().shape({
    label: Yup.string()
      .required(capitalize(translation("freeDays.validation.labelRequired")))
      .min(2, capitalize(translation("freeDays.validation.labelMinLength")))
      .max(120, capitalize(translation("freeDays.validation.labelMaxLength"))),
    dateFrom: Yup.date()
      .required(capitalize(translation("freeDays.validation.dateFromRequired")))
      .nullable(),
    dateTo: Yup.date()
      .required(capitalize(translation("freeDays.validation.dateToRequired")))
      .nullable()
      .test(
        "date-after-from",
        capitalize(translation("freeDays.validation.dateToAfterFrom")),
        function (value) {
          const { dateFrom } = this.parent;
          if (!value || !dateFrom) return true;
          return !isBefore(value, dateFrom);
        }
      ),
    brzKey: Yup.string().required(),
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const payload = {
        label: values.label,
        dateFrom: values.dateFrom
          ? dayjs(values.dateFrom).format("YYYY-MM-DD")
          : "",
        dateTo: values.dateTo ? dayjs(values.dateTo).format("YYYY-MM-DD") : "",
        brzKey: values.brzKey,
      };

      if (isEdit && freeDay) {
        await updateFreeDay.mutateAsync({
          label: payload.label,
          dateFrom: payload.dateFrom,
          dateTo: payload.dateTo,
          brzKey: payload.brzKey,
        });
        enqueueSnackbar(capitalize(translation("freeDays.updateSuccess")), {
          variant: "success",
        });
      } else {
        const createPayload: FreeDayCreate = payload;
        await createFreeDay.mutateAsync(createPayload);
        enqueueSnackbar(capitalize(translation("freeDays.createSuccess")), {
          variant: "success",
        });
      }

      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || capitalize(translation("freeDays.saveError")),
        {
          variant: "error",
        }
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEdit
            ? capitalize(translation("freeDays.editTitle"))
            : capitalize(translation("freeDays.createTitle"))}
        </DialogTitle>
        {freeDay?.id && <FreeDayMessage id={freeDay?.id} />}
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik: FormikProps<FormValues>) => (
            <Form>
              <DialogContent>
                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Field name="label">
                        {({ field, meta }: any) => (
                          <TextField
                            {...field}
                            label={capitalize(translation("label"))}
                            fullWidth
                            error={meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error}
                            variant="outlined"
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid size={12}>
                      <Box
                        sx={{ display: "grid", gridTemplateColumns: ["1fr"] }}
                      >
                        <BrzKeySelect required />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field name="dateFrom">
                        {({ field }: any) => (
                          <DatePicker
                            label={capitalize(translation("freeDays.dateFrom"))}
                            value={field.value}
                            onChange={(date) =>
                              formik.setFieldValue("dateFrom", date)
                            }
                            maxDate={formik.values.dateTo || undefined}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Field name="dateTo">
                        {({ field }: any) => (
                          <DatePicker
                            label={capitalize(translation("freeDays.dateTo"))}
                            value={field.value}
                            onChange={(date) =>
                              formik.setFieldValue("dateTo", date)
                            }
                            minDate={formik.values.dateFrom || undefined}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>

              <DialogActions>
                <Button onClick={onClose} color="secondary">
                  {capitalize(translation("common.cancel"))}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={formik.isSubmitting}
                >
                  {isEdit
                    ? capitalize(translation("common.save"))
                    : capitalize(translation("common.create"))}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </LocalizationProvider>
  );
}
function useSaveFreieZeit(id: string) {
  const updateFreeDay = useUpdateFreeDay();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (input: FreeDayUpdate) => {
      return updateFreeDay(id, input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["freeDays"]);
        navigate(`/freie-zeit`);
      },
    }
  );
}
function useCreateFreieZeit() {
  const createFreeDay = useCreateFreeDay();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (input: FreeDayCreate) => {
      return createFreeDay(input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["freeDays"]);
        navigate(`/freie-zeit`);
      },
    }
  );
}

function BrzKeySelect({ required = false }: { required: boolean }) {
  const { t } = useT("freedays");
  return (
    <Field
      name="brzKey"
      component={Select}
      label={`${capitalize(t("freeDays.brzKey"))} ${required ? "*" : ""}`}
    >
      <MenuItem key="U" value="U">
        U: Urlaub
      </MenuItem>
      <MenuItem key="F" value="F">
        F: Feiertag
      </MenuItem>
    </Field>
  );
}
